import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { videosAPI } from '../utils/api/adminAPI';
import '../styles/VideoPage.css';

const extractYouTubeId = (url = '') => {
  if (!url) {
    return null;
  }

  if (url.includes('youtube.com/embed/')) {
    return url.split('youtube.com/embed/')[1]?.split(/[?&]/)[0] || null;
  }

  const regExp = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^#&?/]{11})/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const getYouTubeThumbnail = (url, fallback = '') => {
  const videoId = extractYouTubeId(url);

  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  return fallback || 'https://placehold.co/640x360/F5EBDD/6B4726?text=Latthsaab+Video';
};

const getEmbedUrl = (url) => {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : null;
};

const formatViews = (views = 0) => {
  if (views >= 1000000) {
    return `${(views / 1000000).toFixed(1)}M views`;
  }

  if (views >= 1000) {
    return `${(views / 1000).toFixed(1)}K views`;
  }

  return `${views} views`;
};

function VideoCategoryPage({
  category,
  badge,
  title,
  subtitle,
  description,
  emptyMessage,
  accentClass
}) {
  const [videos, setVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await videosAPI.getAllVideos(1, 100, category);

      if (!response.success) {
        throw new Error('Videos लोड नहीं हो सकीं');
      }

      const normalizedVideos = (response.videos || []).map((video) => ({
        ...video,
        youtubeId: extractYouTubeId(video.youtubeLink),
        embedUrl: getEmbedUrl(video.youtubeLink),
        thumbnailUrl: getYouTubeThumbnail(video.youtubeLink, video.thumbnail)
      }));

      setVideos(normalizedVideos);

      const firstPlayableVideo = normalizedVideos.find((video) => video.embedUrl) || normalizedVideos[0] || null;
      setSelectedVideoId(firstPlayableVideo?._id || null);
    } catch (err) {
      console.error(`Error fetching ${category} videos:`, err);
      setError(err.message || 'Videos लोड करने में त्रुटि');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const selectedVideo = useMemo(
    () => videos.find((video) => video._id === selectedVideoId) || videos[0] || null,
    [selectedVideoId, videos]
  );

  const playableVideosCount = videos.filter((video) => video.embedUrl).length;

  if (loading) {
    return (
      <div className={`video-page ${accentClass}`}>
        <section className="video-hero-shell">
          <div className="video-hero video-hero-loading">
            <span className="video-badge">{badge}</span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </section>
        <div className="video-container">
          <div className="video-state-card">लोड हो रहा है...</div>
        </div>
      </div>
    );
  }

  if (error || videos.length === 0) {
    return (
      <div className={`video-page ${accentClass}`}>
        <section className="video-hero-shell">
          <div className="video-hero">
            <span className="video-badge">{badge}</span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </section>
        <div className="video-container">
          <div className="video-state-card video-state-error">
            <h2>वीडियो अभी उपलब्ध नहीं हैं</h2>
            <p>{error || emptyMessage}</p>
            <button type="button" className="video-action-button" onClick={fetchVideos}>
              दोबारा कोशिश करें
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`video-page ${accentClass}`}>
      <section className="video-hero-shell">
        <div className="video-hero">
          <div className="video-hero-copy">
            <span className="video-badge">{badge}</span>
            <h1>{title}</h1>
            <p>{subtitle}</p>
            <div className="video-hero-stats">
              <div className="video-stat-pill">
                <strong>{videos.length}</strong>
                <span>कुल वीडियो</span>
              </div>
              <div className="video-stat-pill">
                <strong>{playableVideosCount}</strong>
                <span>तुरंत चलने वाले</span>
              </div>
            </div>
          </div>

          <div className="video-hero-panel">
            <p>{description}</p>
            <button
              type="button"
              className="video-action-button"
              onClick={() => {
                if (selectedVideo?._id) {
                  setSelectedVideoId(selectedVideo._id);
                }
              }}
            >
              अभी देखें
            </button>
          </div>
        </div>
      </section>

      <div className="video-container">
        <section className="video-feature-layout">
          <div className="video-player-card">
            {selectedVideo?.embedUrl ? (
              <>
                <div className="video-player-frame">
                  <iframe
                    src={selectedVideo.embedUrl}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>

                <div className="video-player-details">
                  <div className="video-player-meta">
                    <span>{formatViews(selectedVideo.views)}</span>
                    <span>{new Date(selectedVideo.createdAt).toLocaleDateString('hi-IN')}</span>
                  </div>
                  <h2>{selectedVideo.title}</h2>
                  <p>{selectedVideo.description}</p>
                </div>
              </>
            ) : (
              <div className="video-state-card video-state-warning">
                <h2>इस वीडियो का एम्बेड उपलब्ध नहीं है</h2>
                <p>YouTube लिंक सही format में नहीं है। नीचे सूची से दूसरा वीडियो चुनें।</p>
                {selectedVideo?.youtubeLink && (
                  <a className="video-action-button" href={selectedVideo.youtubeLink} target="_blank" rel="noreferrer">
                    YouTube पर खोलें
                  </a>
                )}
              </div>
            )}
          </div>

          <aside className="video-playlist-card">
            <div className="video-playlist-header">
              <h3>अब चल रहा है</h3>
              <p>किसी भी कार्ड पर क्लिक करके वीडियो यहीं चलाएं</p>
            </div>

            <div className="video-playlist-items">
              {videos.map((video, index) => {
                const isActive = video._id === selectedVideo?._id;

                return (
                  <button
                    key={video._id}
                    type="button"
                    className={`video-playlist-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSelectedVideoId(video._id)}
                  >
                    <div className="video-playlist-thumb">
                      <img src={video.thumbnailUrl} alt={video.title} loading="lazy" />
                      <span className="video-play-index">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="video-playlist-copy">
                      <h4>{video.title}</h4>
                      <p>{video.description}</p>
                      <span>{formatViews(video.views)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        </section>

        <section className="video-grid-section">
          <div className="video-section-heading">
            <div>
              <span className="video-section-eyebrow">वीडियो लाइब्रेरी</span>
              <h2>सभी {title}</h2>
            </div>
            <p>{emptyMessage}</p>
          </div>

          <div className="video-grid">
            {videos.map((video) => {
              const isActive = video._id === selectedVideo?._id;

              return (
                <article
                  key={video._id}
                  className={`video-card ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedVideoId(video._id)}
                >
                  <div className="video-thumbnail">
                    <img src={video.thumbnailUrl} alt={video.title} loading="lazy" />
                    <div className="play-button">▶</div>
                    <div className="views-badge">{formatViews(video.views)}</div>
                  </div>
                  <div className="video-info">
                    <div className="video-card-meta">
                      <span>{category}</span>
                      <span>{new Date(video.createdAt).toLocaleDateString('hi-IN')}</span>
                    </div>
                    <h3>{video.title}</h3>
                    <p>{video.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export default VideoCategoryPage;